/**
 * sibyl, running in the visitor's browser.
 *
 * ⚠️ DEMO ONLY. This file wires the REAL modules from src/ to a sample pull request:
 *   · the console page talks to src/web/server.ts's own request handler (through shims/http.ts),
 *   · Review runs src/engine/run.ts `review()` — diff, anchor set, gates, refuter, fingerprints —
 *     over the sample trees, with the model's answers replayed from demo/scenario.ts,
 *   · `sibyl post`, `sibyl recheck`, `sibyl triage`, `sibyl config` and `sibyl init` are the real
 *     command functions, writing to an in-memory GitHub instead of github.com.
 * Nothing leaves the browser. The only things that are not sibyl are the model's answers and
 * GitHub, and the page says so.
 */

import { process as proc, sink, ExitSignal, exitHook } from "./shims/process"
import * as vfs from "./shims/vfs"
import { dispatch, lastHandler } from "./shims/http"
import { setGitShow } from "./shims/child_process"
import { registerModules } from "./shims/module"
import * as triageCmd from "../src/cli/triage"
import * as initCmd from "../src/cli/init"
import { startServer } from "../src/web/server"
import { loadConfig, describeResolution } from "../src/config/load"
import { review, renderRun, type ReviewDeps } from "../src/engine/run"
import { openTriageStore } from "../src/triage/store"
import { resolveBackend, bannerLine } from "../src/backend/probe"
import { runPostCommand } from "../src/cli/post"
import { runRecheckCommand, readLinesAt, priorQuoteResolver } from "../src/cli/recheck"
import { main } from "../src/cli/index"
import type { Backend, BackendRequest, BackendResult } from "../src/backend/types"
import type { SibylConfig } from "../src/config/schema"
import * as S from "./scenario"
import pAdversarial from "../prompts/adversarial.md"
import pSecurity from "../prompts/security.md"
import pQuality from "../prompts/code-quality.md"
import pPerformance from "../prompts/performance.md"
import pNecessity from "../prompts/necessity.md"

// ── The demo machine ───────────────────────────────────────────────────────────────────────────

const PROMPT_ROOT = "/sibyl"
const PROMPTS = [["adversarial.md", pAdversarial], ["security.md", pSecurity], ["code-quality.md", pQuality], ["performance.md", pPerformance], ["necessity.md", pNecessity]] as const
const DATA = "/home/demo/.local/share/sibyl"
const PROJECT_CONFIG = [
  "# The demo's project config. Everything not set here is sibyl's built-in default —",
  "# run `sibyl config print` to see each value and where it came from.",
  "",
  "[repo]",
  `slug = "${S.REPO}"`,
  "",
  "# The demo posts to a simulated GitHub, so the two posting locks are released here.",
  "# triage.require_human stays on: only findings a person accepted are ever posted.",
  "[post]",
  "enabled = true",
  "dry_run = false",
  "",
].join("\n")

/** What a visitor sets in the Credentials panel. Session-only, never sent anywhere. */
const ENV_KEY = "sibyl-demo-env-v1"
const DEFAULT_ENV: Record<string, string> = {
  USER: "you",
  GITHUB_TOKEN: "demo-token-not-real-gh42",
  ANTHROPIC_API_KEY: "demo-key-not-real-a7k3",
}
let env: Record<string, string> = { ...DEFAULT_ENV }

interface DemoState {
  /** Which push of the pull request is the head: the first, or after the author's fix. */
  head: S.Version
  /** How many reviews have run, so each one is a new run in the log. */
  reviews: number
  /** Everything the simulated GitHub received, newest last. */
  github: GitHubEvent[]
}
export interface GitHubEvent {
  kind: "review" | "status" | "label"
  at: string
  body: Record<string, unknown>
  path: string
}
const STATE_KEY = "sibyl-demo-meta-v1"
let state: DemoState = { head: "v1", reviews: 0, github: [] }

function save(): void {
  vfs.saveState()
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify(state))
    sessionStorage.setItem(ENV_KEY, JSON.stringify(env))
  } catch {
    /* storage unavailable */
  }
}

function writeTree(root: string, tree: Record<string, readonly string[]>): void {
  for (const [p, lines] of Object.entries(tree)) vfs.put(`${root}/${p}`, lines.join("\n"))
}

const CLONES = "/home/demo/.cache/sibyl/clones/acme-invoicing"
const SHA_TREE: Record<string, S.Version | "base"> = {
  [S.SHA.base]: "base",
  [S.SHA.head1]: "v1",
  [S.SHA.merge1]: "v1",
  [S.SHA.head2]: "v2",
  [S.SHA.merge2]: "v2",
}

function setupMachine(): void {
  // src/cli/index.ts loads its commands with require(); review, post and recheck are routed by runCli.
  registerModules({ "triage.js": triageCmd, "init.js": initCmd })
  vfs.put(`${PROMPT_ROOT}/prompts/adversarial.md`, pAdversarial)
  vfs.put(`${PROMPT_ROOT}/prompts/security.md`, pSecurity)
  vfs.put(`${PROMPT_ROOT}/prompts/code-quality.md`, pQuality)
  vfs.put(`${PROMPT_ROOT}/prompts/performance.md`, pPerformance)
  vfs.put(`${PROMPT_ROOT}/prompts/necessity.md`, pNecessity)
  // A project config anchors relative prompt paths at its own directory (src/config/load.ts), so the
  // shipped prompts are also where /work/.sibyl/sibyl.toml resolves them.
  for (const [name, text] of PROMPTS) {
    vfs.put(`/work/.sibyl/prompts/${name}`, text)
  }
  vfs.put("/work/.sibyl/sibyl.toml", PROJECT_CONFIG)
  for (const [sha, v] of Object.entries(SHA_TREE)) writeTree(`${CLONES}/${sha}`, S.TREES[v])
  // `git show <sha>:<path>` — used by `sibyl recheck` to recover the quote a stored record does not keep.
  setGitShow((spec) => {
    const [sha, ...rest] = spec.split(":")
    const v = SHA_TREE[sha ?? ""]
    const lines = v === undefined ? undefined : S.TREES[v][rest.join(":")]
    return lines === undefined ? undefined : `${lines.join("\n")}\n`
  })
  proc.env["XDG_DATA_HOME"] = undefined
}

// ── GitHub, simulated ──────────────────────────────────────────────────────────────────────────

function headSha(): string {
  return state.head === "v1" ? S.SHA.head1 : S.SHA.head2
}
function mergeSha(): string {
  return state.head === "v1" ? S.SHA.merge1 : S.SHA.merge2
}

function json(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), { status, headers: { "content-type": "application/json" } })
}

async function fakeGitHub(url: URL, init?: RequestInit): Promise<Response> {
  const p = url.pathname
  const repo = `/repos/${S.REPO}`
  const method = (init?.method ?? "GET").toUpperCase()
  const files = S.changedFiles(state.head)
  if (method === "GET" && p === repo) {
    // The sample repository is public, so sibyl's licence gate lets it through on the free tier.
    return json(200, { full_name: S.REPO, private: false, default_branch: "main" })
  }
  if (method === "GET" && p === `${repo}/pulls/${S.PR_NUMBER}`) {
    return json(200, {
      number: S.PR_NUMBER,
      title: S.PR_TITLE,
      user: { login: S.AUTHOR },
      head: { sha: headSha(), ref: "approve-from-list" },
      base: { sha: S.SHA.base, ref: "main" },
      draft: false,
      state: "open",
      changed_files: files.length,
    })
  }
  if (method === "GET" && p === `${repo}/pulls/${S.PR_NUMBER}/files`) {
    const page = Number(url.searchParams.get("page") ?? "1")
    return json(
      200,
      page === 1 ? files.map((f) => ({ filename: f.path, status: f.status, additions: f.additions, deletions: f.deletions, patch: f.patch })) : [],
    )
  }
  if (method === "GET" && p.startsWith(`${repo}/pulls/`)) {
    return json(404, { message: `Not Found — this demo has one pull request, #${S.PR_NUMBER}` })
  }
  if (method === "POST" && p === `${repo}/pulls/${S.PR_NUMBER}/reviews`) {
    const body = JSON.parse(String(init?.body ?? "{}")) as Record<string, unknown>
    state.github.push({ kind: "review", at: new Date().toISOString(), body, path: p })
    save()
    return json(200, { id: 900000 + state.github.length, html_url: `https://github.com/${S.REPO}/pull/${S.PR_NUMBER}` })
  }
  if (method === "POST" && p.startsWith(`${repo}/statuses/`)) {
    const body = JSON.parse(String(init?.body ?? "{}")) as Record<string, unknown>
    state.github.push({ kind: "status", at: new Date().toISOString(), body: { ...body, sha: p.split("/").pop() }, path: p })
    save()
    return json(201, { id: state.github.length, ...body })
  }
  return json(404, { message: `the demo's GitHub has no ${method} ${p}` })
}

// ── The model, replayed ────────────────────────────────────────────────────────────────────────

export interface ModelEvent {
  kind: "start" | "done"
  dimension: string
  refute: boolean
  findings?: number
  verdict?: string
}

function lineOf(version: S.Version, path: string, text: string): number {
  const i = (S.TREES[version][path] ?? []).indexOf(text)
  if (i < 0) throw new Error(`demo scenario: "${text}" is not in ${path} at ${version}`)
  return i + 1
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

function scriptedBackend(kind: "claude_cli" | "anthropic_api", cfg: SibylConfig, version: S.Version, onEvent: (e: ModelEvent) => void, pace: number): Backend {
  const model = kind === "claude_cli" ? cfg.backend.claudeCli.model : cfg.backend.anthropicApi.model
  const effort = kind === "claude_cli" ? cfg.backend.claudeCli.effort : cfg.backend.anthropicApi.effort
  const result = (dimensionId: string, output: unknown, inputTokens: number, outputTokens: number): BackendResult =>
    ({
      backend: kind,
      dimensionId,
      model,
      effort,
      output,
      raw: JSON.stringify(output),
      stopReason: "end_turn",
      usage: { inputTokens, outputTokens },
      cache: { createdTokens: 0, readTokens: 0, uncachedInputTokens: inputTokens, created1hTokens: 0, created5mTokens: 0, outcome: "miss" },
      durationMs: 0,
      permissionDenials: [],
    }) as unknown as BackendResult

  return {
    kind,
    async run(req: BackendRequest): Promise<BackendResult> {
      if (req.dimensionId.startsWith("refute:")) {
        const dim = req.dimensionId.slice("refute:".length)
        onEvent({ kind: "start", dimension: dim, refute: true })
        await sleep(350 * pace)
        const f = S.MODEL[version].find((x) => x.dimension === dim && req.dimensionInstruction.includes(x.title))
        let out: Record<string, unknown>
        if (f?.refuted !== undefined) {
          const line = lineOf(version, f.refuted.path, f.refuted.line)
          out = {
            reasoning: "Read the function the finding cites and the code that calls it.",
            why: f.refuted.why,
            disproof: { path: f.refuted.path, line, quote: f.refuted.line },
            verdict: "refuted",
          }
        } else {
          out = { reasoning: "Read the cited code and tried to construct the counter-case.", why: "the cited code does what the finding says, and nothing upstream prevents it", verdict: "stands" }
        }
        onEvent({ kind: "done", dimension: dim, refute: true, verdict: String(out["verdict"]) })
        return result(req.dimensionId, out, 2_100, 180)
      }
      onEvent({ kind: "start", dimension: req.dimensionId, refute: false })
      await sleep(900 * pace)
      const mine = S.MODEL[version].filter((f) => f.dimension === req.dimensionId)
      const findings = mine.map((f) => ({
        reasoning: f.reasoning,
        title: f.title,
        path: f.path,
        line: lineOf(version, f.path, f.line),
        quote: f.misquote ?? f.line,
        consequence: f.consequence,
        failureScenario: { inputs: f.inputs, wrongOutcome: f.wrongOutcome },
        recommendedFix: f.fix,
        effort: f.effort,
        ...(f.rule === undefined ? {} : { ruleCitation: { source: "SIBYL.md", text: f.rule } }),
        severity: f.severity,
      }))
      onEvent({ kind: "done", dimension: req.dimensionId, refute: false, findings: findings.length })
      return result(req.dimensionId, { findings }, 14_000 + 900 * findings.length, 600 + 450 * findings.length)
    },
  }
}

// ── Running commands ───────────────────────────────────────────────────────────────────────────

export interface Term {
  out: (s: string) => void
  err: (s: string) => void
}

/** One command at a time: stdout and stderr are a single pair of sinks, as on a real terminal. */
let running: Promise<unknown> = Promise.resolve()

async function captured<T>(t: Term, fn: () => Promise<T> | T): Promise<T | number> {
  const before = running
  let release = (): void => {}
  running = new Promise<void>((r) => (release = r))
  await before
  try {
    return await capturedNow(t, fn)
  } finally {
    release()
  }
}

async function capturedNow<T>(t: Term, fn: () => Promise<T> | T): Promise<T | number> {
  const prev = { out: sink.out, err: sink.err }
  sink.out = t.out
  sink.err = t.err
  try {
    return await fn()
  } catch (e) {
    if (e instanceof ExitSignal) return e.code
    t.err(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
    return 70
  } finally {
    sink.out = prev.out
    sink.err = prev.err
  }
}

function config(configPath?: string): SibylConfig {
  return loadConfig(configPath === undefined ? {} : { configPath }).config
}

/** The console's own choices — backend, ref, dimensions — applied as `sibyl review` flags would be. */
export interface ReviewChoices {
  pr: number
  backend: "claude_cli" | "anthropic_api" | "auto"
  /** False on the simulated CI runner, which has no `claude` binary. */
  cliInstalled?: boolean
  ref: "merge" | "head"
  dimensions: Record<string, boolean>
  pace?: number
  onEvent?: (e: ModelEvent) => void
  /** `--config <path>`, as the GitHub workflow passes it. */
  configPath?: string
}

/** `sibyl review <pr>`, as src/cli/review.ts runs it, with the clone and the model supplied by the demo. */
export async function runReview(t: Term, c: ReviewChoices & { repo?: string }): Promise<number> {
  const rc = await captured(t, async () => {
    const base = config(c.configPath)
    const cfg: SibylConfig = {
      ...base,
      backend: c.backend === "auto" ? base.backend : { ...base.backend, default: c.backend },
      dimensions: base.dimensions.map((d) => (d.id in c.dimensions ? { ...d, enabled: c.dimensions[d.id] === true } : d)),
    }
    if ((c.repo ?? S.REPO) !== S.REPO) {
      t.err(`⛔ could not read ${c.repo}#${c.pr}: GitHub 404 — this demo has one repository, ${S.REPO}\n`)
      return 69
    }
    if (c.pr !== S.PR_NUMBER) {
      t.err(`⛔ could not read ${S.REPO}#${c.pr}: GitHub 404 — this demo has one pull request, #${S.PR_NUMBER}\n`)
      return 69
    }
    // The real banner, from the real probe: the CLI probe is answered as a signed-in claude CLI.
    const resolution = await resolveBackend(cfg, {
      lookupCredential: (n) => env[n],
      spawn: async () =>
        c.cliInstalled === false
          ? { stdout: "", stderr: "", code: null, spawnError: Object.assign(new Error("spawn claude ENOENT"), { code: "ENOENT" }) }
          : { stdout: JSON.stringify({ loggedIn: true, authMethod: "claude.ai", subscriptionType: "max" }), stderr: "", code: 0 },
    })
    t.err(`${bannerLine(resolution)}\n`)
    if (resolution.backend === "none") return 69
    const store = await openTriageStore({ path: cfg.triage.db })
    t.err(`triage: ${store.backend.kind} at ${store.backend.path} — ${store.backend.reason}\n`)
    const version = state.head
    const sha = c.ref === "merge" ? mergeSha() : headSha()
    const deps: ReviewDeps = {
      pullRequest: async (r, n) => {
        const res = await fakeGitHub(new URL(`https://api.github.com/repos/${r}/pulls/${n}`))
        const d = (await res.json()) as Record<string, any>
        return { number: d["number"], title: d["title"], headSha: d["head"]["sha"], baseSha: d["base"]["sha"], baseRef: d["base"]["ref"], draft: false, state: d["state"], changedFiles: d["changed_files"] }
      },
      changedFiles: async () => S.changedFiles(version).map((f) => ({ path: f.path, status: f.status, additions: f.additions, deletions: f.deletions, patch: f.patch })),
      prepareClone: async () => ({
        path: `${CLONES}/${sha}`,
        sha,
        ref: c.ref === "merge" ? `refs/pull/${S.PR_NUMBER}/merge` : `refs/pull/${S.PR_NUMBER}/head`,
        usedFallbackRef: false,
        reused: state.reviews > 0,
        pruned: [],
      }),
      backend: scriptedBackend(resolution.backend, cfg, version, c.onEvent ?? (() => {}), c.pace ?? 1),
      promptRoot: PROMPT_ROOT,
      upsert: (findings, s) => {
        const r = store.upsert(findings, { sha: s })
        return { created: r.created.length, carriedForward: r.carriedForward.length }
      },
      isDismissed: (fp) => store.isDismissed(fp),
      log: (line) => t.err(`   ${line}\n`),
    }
    try {
      const run = await review({ repo: S.REPO, pr: c.pr, config: cfg, deps })
      t.out(`${renderRun(run)}\n`)
      t.out(`\nNothing has been posted. Review the queue with \`sibyl triage\`, or open \`sibyl web\`.\n`)
      state.reviews++
      return run.failures.length > 0 ? 69 : 0
    } finally {
      store.close()
    }
  })
  await restartServer()
  save()
  return rc as number
}

/** `sibyl post <pr>` — the real command, against the simulated GitHub. */
export async function runPost(t: Term, argv: string[], configPath?: string): Promise<number> {
  const pr = Number(argv.find((a) => /^\d+$/.test(a)) ?? S.PR_NUMBER)
  const r = argv.indexOf("--repo")
  const repo = r >= 0 ? argv[r + 1] : undefined
  const rc = await captured(t, () =>
    runPostCommand({
      ...(repo === undefined ? {} : { repo }),
      pr,
      config: config(configPath),
      env,
      argv,
      out: t.out,
      err: t.err,
      fetchImpl: ((u: string | URL, init?: RequestInit) => fakeGitHub(new URL(String(u)), init)) as typeof fetch,
    }),
  )
  await restartServer()
  save()
  return rc as number
}

/** `sibyl recheck <pr>` — the real command; the clone and `git show` come from the sample trees. */
export async function runRecheck(t: Term, argv: string[]): Promise<number> {
  const cfg = config()
  const rc = await captured(t, async () => {
    const store = await openTriageStore({ path: cfg.triage.db })
    t.err(`triage: ${store.backend.kind} at ${store.backend.path} — ${store.backend.reason}\n`)
    try {
      return await runRecheckCommand({
        config: cfg,
        argv,
        env,
        out: t.out,
        err: t.err,
        deps: {
          prepareClone: async () => ({ path: `${CLONES}/${mergeSha()}`, sha: mergeSha(), ref: `refs/pull/${S.PR_NUMBER}/merge`, usedFallbackRef: false, reused: true, pruned: [] }),
          changedPaths: async () => S.changedFiles(state.head).map((f) => f.path),
          priorRecords: async (clone) => {
            const quoteOf = priorQuoteResolver(clone.path)
            return Promise.all(store.all().map(async (record) => ({ record, quote: await quoteOf(record) })))
          },
          readLines: readLinesAt,
          log: (line) => t.err(`   ${line}\n`),
        },
      })
    } finally {
      store.close()
    }
  })
  return rc as number
}

/** Anything else is `main()` from src/cli/index.ts, exactly as the binary runs it. */
export async function runCli(t: Term, argv: string[]): Promise<number> {
  const [cmd] = argv
  if (cmd === "review") {
    const pr = Number(argv.find((a) => /^\d+$/.test(a)))
    if (!Number.isFinite(pr)) {
      t.err("⛔ `sibyl review` needs a pull-request number\n")
      return 64
    }
    return runReview(t, { pr, backend: "claude_cli", ref: "merge", dimensions: {} , pace: 0.4 })
  }
  if (cmd === "post") return runPost(t, argv.slice(1))
  if (cmd === "recheck") return runRecheck(t, argv.slice(1))
  if (cmd === "web") {
    const cfg = config()
    t.out(`sibyl console on http://${cfg.server.bind}:${cfg.server.port}\n`)
    t.out(`the queue is read from ${cfg.triage.db}\n`)
    t.out(`(in this demo, the console is the page you are on)\n`)
    return 0
  }
  const rc = (await captured(t, async () => {
    const r = main(argv)
    if (r !== -1) return r
    // STAY_ALIVE: the command finishes on its own and ends with process.exit(rc).
    return await new Promise<number>((done) => {
      exitHook.fn = (code) => {
        exitHook.fn = undefined
        done(code)
      }
    })
  })) as number
  if (cmd === "triage") await restartServer()
  save()
  return rc
}

// ── The GitHub workflow (templates/sibyl-review.yml), on a simulated runner ─────────────────────

/** The config the shipped workflow writes before it runs (templates/sibyl-review.yml, "Configure"). */
const WORKFLOW_TOML = [
  "[post]",
  "enabled = true",
  "dry_run = false",
  "[post.commit_status]",
  "enabled = true",
  'context = "sibyl"',
  "[triage]",
  "require_human = false",
  "",
].join("\n")

/**
 * Somebody adds the `sibyl` label: the workflow takes the label off, sets the check to pending, runs
 * `sibyl review` and `sibyl post` with the workflow's config on a fresh runner, and the check resolves.
 */
export async function runLabelWorkflow(t: Term, onEvent?: (e: ModelEvent) => void): Promise<number> {
  const RUNNER = "/home/runner"
  vfs.remove(RUNNER)
  vfs.put(`${RUNNER}/work/sibyl/sibyl.toml`, WORKFLOW_TOML)
  // The workflow runs inside its checkout of sibyl, so the shipped prompts sit beside sibyl.toml.
  for (const [name, text] of PROMPTS) vfs.put(`${RUNNER}/work/sibyl/prompts/${name}`, text)
  const home = proc.env["HOME"]
  const cwd = proc.cwd
  proc.env["HOME"] = RUNNER
  proc.cwd = () => `${RUNNER}/work/sibyl`
  state.github.push({ kind: "label", at: new Date().toISOString(), body: { action: "added and removed", label: "sibyl" }, path: "label" })
  t.out(`Run: Take the label off, so asking again is possible\n`)
  t.out(`Run: Say that a review is running\n`)
  await fakeGitHub(new URL(`https://api.github.com/repos/${S.REPO}/statuses/${headSha()}`), {
    method: "POST",
    body: JSON.stringify({ state: "pending", context: "sibyl", description: "Reviewing…" }),
  })
  t.out(`Run: sibyl review ${S.PR_NUMBER} --repo ${S.REPO} --config sibyl.toml\n`)
  try {
    const rc = await runReview(t, {
      pr: S.PR_NUMBER,
      repo: S.REPO,
      backend: "auto",
      cliInstalled: false,
      ref: "merge",
      dimensions: {},
      pace: 0.5,
      configPath: `${RUNNER}/work/sibyl/sibyl.toml`,
      ...(onEvent === undefined ? {} : { onEvent }),
    })
    if (rc !== 0) throw new Error(`review exited ${rc}`)
    t.out(`\nRun: sibyl post ${S.PR_NUMBER} --repo ${S.REPO} --config sibyl.toml\n`)
    const prc = await runPost(t, [String(S.PR_NUMBER), "--repo", S.REPO], `${RUNNER}/work/sibyl/sibyl.toml`)
    if (prc !== 0) throw new Error(`post exited ${prc}`)
    return 0
  } catch (e) {
    await fakeGitHub(new URL(`https://api.github.com/repos/${S.REPO}/statuses/${headSha()}`), {
      method: "POST",
      body: JSON.stringify({ state: "error", context: "sibyl", description: "sibyl could not complete — see the run" }),
    })
    t.err(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
    return 1
  } finally {
    if (home === undefined) delete proc.env["HOME"]
    else proc.env["HOME"] = home
    proc.cwd = cwd
    await restartServer()
    save()
  }
}

/** The pull request's checks, as GitHub would show them: the latest status per context. */
export function checks(): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {}
  for (const e of state.github) if (e.kind === "status" && e.body["sha"] === headSha()) out[String(e.body["context"])] = e.body
  return out
}
export function currentHead(): string {
  return headSha()
}

// ── The console's server ───────────────────────────────────────────────────────────────────────

let server: { close(): void } | undefined
let handler: typeof lastHandler

async function restartServer(): Promise<void> {
  server?.close()
  server = startServer({ config: config(), env: { ...env } }) as unknown as { close(): void }
  handler = lastHandler
}

export async function serverFetch(url: string, init?: RequestInit): Promise<Response> {
  if (handler === undefined) await restartServer()
  const r = await dispatch(handler!, url, (init?.method ?? "GET").toUpperCase(), String(init?.body ?? ""))
  if (url.startsWith("/api/triage") && r.status === 200) setTimeout(save, 0)
  return new Response(r.body, { status: r.status, headers: r.headers })
}

// ── Lifecycle ──────────────────────────────────────────────────────────────────────────────────

export function getState(): Readonly<DemoState> {
  return state
}
export function getEnv(): Readonly<Record<string, string>> {
  return env
}
export async function setCredential(name: string, value: string): Promise<void> {
  if (value === "") delete env[name]
  else env[name] = value
  proc.env[name] = value === "" ? undefined : value
  await restartServer()
  save()
}
/** The author pushes again: the head moves to the second version of the pull request. */
export function authorPushes(): void {
  state.head = "v2"
  save()
}
export function pushedPaths(): string[] {
  return S.pushedPaths()
}
export async function reset(): Promise<void> {
  vfs.clearState()
  try {
    sessionStorage.removeItem(STATE_KEY)
    sessionStorage.removeItem(ENV_KEY)
  } catch {
    /* nothing to clear */
  }
  env = { ...DEFAULT_ENV }
  for (const k of Object.keys(env)) proc.env[k] = env[k]
  state = { head: "v1", reviews: 0, github: [] }
  await firstReview()
}

/** The populated starting point: the first push of the pull request has been reviewed once. */
async function firstReview(): Promise<void> {
  let log = ""
  const quiet: Term = { out: (s) => (log += s), err: (s) => (log += s) }
  const rc = await runReview(quiet, { pr: S.PR_NUMBER, backend: "claude_cli", ref: "merge", dimensions: {}, pace: 0 })
  // ⛔ A starting review that failed must be visible, not an empty queue that reads as "nothing found".
  if (rc !== 0) throw new Error(`the starting review exited ${rc}:\n${log}`)
}

export async function boot(): Promise<void> {
  setupMachine()
  let restored = false
  try {
    const m = sessionStorage.getItem(STATE_KEY)
    const e = sessionStorage.getItem(ENV_KEY)
    if (m !== null && vfs.loadState()) {
      state = JSON.parse(m) as DemoState
      if (e !== null) env = JSON.parse(e) as Record<string, string>
      restored = true
    }
  } catch {
    restored = false
  }
  for (const k of Object.keys(env)) proc.env[k] = env[k]
  if (!restored) await firstReview()
  await restartServer()
}

export const scenario = { REPO: S.REPO, PR_NUMBER: S.PR_NUMBER, PR_TITLE: S.PR_TITLE, AUTHOR: S.AUTHOR, SHA: S.SHA }
export { describeResolution }
